/**
 * CVSS base score — one presentation for every table: the number, and under it
 * a short bar filled to score/10 in the severity colour (CVSS v3.1 bands,
 * docs/design/function-colors.md). Numeric column, so it is right-aligned;
 * in the stacked mobile layout it aligns left.
 *
 * In running text (fact lists, audit entries) use a chip instead:
 * `<Chip tone={cvssTone(score)}>CVSS {fmtScore(score)}</Chip>`.
 */

import type { ReactNode } from 'react';

import type { ChipTone } from './Chip';
import { DASH, fmtScore } from '../lib/format';

export type CvssLevel = 'critical' | 'high' | 'medium' | 'low' | 'info';

/** Severity band of a CVSS v3.1 base score. */
export function cvssLevel(score: number): CvssLevel {
  if (score >= 9) return 'critical';
  if (score >= 7) return 'high';
  if (score >= 4) return 'medium';
  if (score > 0) return 'low';
  return 'info';
}

/** Chip tone for a score in running text; Kritisch and Hoch share Alarm Rot. */
export function cvssTone(score: number | null): ChipTone {
  if (score === null || !Number.isFinite(score)) return 'neutral';
  const level = cvssLevel(score);
  if (level === 'critical' || level === 'high') return 'alert';
  if (level === 'medium') return 'warn';
  if (level === 'low') return 'info';
  return 'neutral';
}

export interface CvssScoreProps {
  score: number | null | undefined;
  /** Tooltip, e.g. the vector or the score's source. */
  title?: string;
}

export function CvssScore({ score, title }: CvssScoreProps): ReactNode {
  if (score === null || score === undefined || !Number.isFinite(score)) {
    return <span className="muted">{DASH}</span>;
  }
  const width = Math.max(0, Math.min(score, 10)) * 10;
  return (
    <span className={`cvss-score cvss-${cvssLevel(score)}`} title={title}>
      <strong>{fmtScore(score)}</strong>
      <span className="cvss-score-track" aria-hidden="true">
        <span style={{ width: `${width}%` }} />
      </span>
    </span>
  );
}

export default CvssScore;
